import { ElementRef, Injectable, inject } from '@angular/core';
import { Observer, catchError, take } from 'rxjs';
import { ExchangeService } from '../../_shared/data-access/exchange.service';
import { ModalService } from '../../_shared/data-access/modal.service';
import { getMessageError } from '../../_shared/utils/get-message-error';
import { EventsService } from '../../events/data-access/events.service';
import { PaymentAPI } from './_payment.api';
import { PaymentService } from './payment.service';

/**
 * BraintreeService is an injectable service in charge of managing Braintree payment integrations.
 * It leverages the Braintree Web Drop-in to provide a customizable payment UI. It integrates with
 * PaymentAPI for Braintree token retrieval and transaction handling and with PaymentService for
 * transaction state management. It also uses Router for navigation upon successful or unsuccessful payment confirmations.
 */
@Injectable({
  providedIn: 'root',
})
export class BraintreeService {
  private _api = inject(PaymentAPI);
  private _paymentService = inject(PaymentService);
  private _modalService = inject(ModalService);
  private _eventService = inject(EventsService);
  private _exchangeService = inject(ExchangeService);

  public braintreeInstance: { requestPaymentMethod: () => any } | null = null;

  /**
   * Mounts the Braintree drop-in component into a specified container element.
   * It retrieves a Braintree token and initializes the drop-in component.
   * @param container The ElementRef where the Braintree drop-in UI will be mounted.
   * @param factory The function to create a Braintree drop-in instance.
   */
  public mountBraintreeComponent = (container: ElementRef, factory: any) => {
    if (this.braintreeInstance) {
      return;
    }

    this.getBraintreeToken()
      ?.pipe(take(1), catchError(this._paymentService.onPaymentProviderError))
      .subscribe((token: string | undefined) => {
        void this.setBraintreeInstance(token, container, factory);
      });
  };

  /**
   * Confirms the payment through Braintree. It requests a payment method from the Braintree instance
   * and proceeds to check out using the obtained payment method nonce.
   * @param setIsProccessing A callback to indicate whether the payment process is ongoing.
   */
  public async confirmPayment(setIsProccessing: (isProcessing: boolean) => void) {
    const instance = this.braintreeInstance;
    if (!instance) {
      return;
    }

    setIsProccessing(true);

    const observer: Partial<Observer<any>> = {
      next: (checkoutData: { oldPrice: number; newPrice: number; row: string; seat: string; section: string }[]) => {
        const computedCheckoutData = checkoutData.map(data => {
          const price = this._exchangeService.isTransferredSelection()
            ? data.newPrice
            : this._exchangeService.computePriceWithExchangeType(data.oldPrice, data.newPrice);

          return {
            ...data,
            price,
          };
        });

        this._paymentService.purchaseData = computedCheckoutData;
        this._paymentService.isPaymentAccepted = true;
        this._paymentService.handlePaymentSuccess();
        setIsProccessing(false);
      },
      error: error => {
        // get request error here

        this._paymentService.errorMessage = this.getErrorConfiguration(error)!;
        this._paymentService.isPaymentAccepted = true;
        this._paymentService.handlePaymentFail();
        setIsProccessing(false);
      },
    };

    try {
      const payload = await instance.requestPaymentMethod();
      const observable = this._paymentService.checkoutOnRequest(payload.nonce, payload.details.cardType);
      observable.subscribe(observer);
    } catch {
      const modalData = {
        type: 'app',
        text: 'Payment information is invalid. Review your credit card and expiry date details.',
        buttonText: 'Retry',
        buttonAction: () => this._modalService.closeModal(),
      };
      setIsProccessing(false);
      this._modalService.openModal(modalData);
    }
  }
  /**
   * Determines the appropriate configuration for the modal based on the error message.
   *
   * @param  errorMessage - The error message received from the error object.
   * @returns An object containing the modal configuration.
   */
  private getErrorConfiguration(error: { error?: { code: string; message: string } }) {
    return getMessageError(error.error!);
  }

  /**
   * Reset Braintree component back to its null state
   */

  public resetBraintreeComponent = () => {
    this.braintreeInstance = null;
  };

  /**
   * Retrieves a Braintree token necessary to initialize the Braintree drop-in component.
   * @returns An Observable that emits the Braintree token.
   */
  private getBraintreeToken = () => {
    const cartId = this._paymentService.getTransaction()?.cartId;
    if (!cartId) {
      return;
    }

    return this._api.getBraintreeToken(cartId);
  };

  /**
   * Sets the Braintree instance after initializing it with the given token and container.
   * @param token The Braintree client token.
   * @param container The ElementRef where the Braintree drop-in UI will be mounted.
   * @param factory The function to create a Braintree drop-in instance.
   */
  private setBraintreeInstance = async (token: string | undefined, container: ElementRef, factory: any) => {
    if (token === undefined) {
      return;
    }

    const options = {
      authorization: token,
      container: container?.nativeElement,
    };

    const whenInstance = await factory(options);
    this.braintreeInstance = whenInstance;

    this.focusCardInputOnLoad();
  };

  /**
   * Sets the Braintree credit card input as focused.
   */
  private focusCardInputOnLoad = () => {
    const cardForm = document.querySelector('#braintree-hosted-field-number') as HTMLIFrameElement;

    if (cardForm) {
      cardForm?.focus();
      cardForm?.click();
      cardForm.contentWindow?.focus();
      document.activeElement?.dispatchEvent(
        new KeyboardEvent('keypress', {
          key: 'Tab',
        })
      );
    }
  };
}
